import React, { useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./mealplanimage.module.css";
import { ImageKey, LABEL, ORDER_K } from "./mealplanutil";

//식단표 이미지 전체 화면 미리보기 — ‹ › · ← → 로 4장 넘기기, Esc · × · 배경 클릭으로 닫기
//body 아래로 포털 (대시보드 레이아웃 transform/overflow 영향 안 받게)

interface Props {
    open: boolean;
    imageKey: ImageKey;
    images: { [k: string]: HTMLCanvasElement };
    subtitle: string;               //"송파점 10월 4주차"
    onChange: (k: ImageKey) => void;
    onClose: () => void;
    onDownload: (k: ImageKey) => void;
}

const DownloadIcon = (props: { size: number }) => (
    <svg width={props.size} height={props.size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    </svg>
);

const MealPlanPreviewModal: React.FC<Props> = (props) => {

    const closeRef = useRef<HTMLButtonElement>(null);
    const cv = props.images[props.imageKey];

    const src = useMemo(() => (props.open && cv ? cv.toDataURL("image/png") : ""), [props.open, cv]);

    const step = (n: number) => {
        const i = ORDER_K.indexOf(props.imageKey);
        props.onChange(ORDER_K[(i + n + 4) % 4]);
    };

    useEffect(() => {
        if (!props.open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") props.onClose();
            if (e.key === "ArrowRight") step(1);
            if (e.key === "ArrowLeft") step(-1);
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [props.open, props.imageKey]);

    useEffect(() => {
        if (props.open && closeRef.current) closeRef.current.focus();
    }, [props.open]);

    if (!props.open || !cv) return null;

    return ReactDOM.createPortal(
        <div className={styles.modal} role="dialog" aria-modal="true" aria-label="식단표 이미지 미리보기">
            <div className={styles.mhead}>
                <div className={styles.ttl}>
                    <span>{LABEL[props.imageKey]}</span>
                    <small>{`${cv.width}×${cv.height} · PNG · ${props.subtitle}`}</small>
                </div>
                <div className={styles.acts}>
                    <button className={`${styles.mbtn} ${styles.primary}`} type="button" onClick={() => props.onDownload(props.imageKey)}>
                        <DownloadIcon size={15} /><span className={styles.hideNarrow}>이 이미지 저장</span>
                    </button>
                    <button className={`${styles.mbtn} ${styles.x}`} type="button" aria-label="닫기" ref={closeRef} onClick={props.onClose}>×</button>
                </div>
            </div>
            <div className={styles.mbody} onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }}>
                <button className={`${styles.navB} ${styles.l}`} type="button" aria-label="이전 이미지" onClick={() => step(-1)}>‹</button>
                <img src={src} alt={`${props.subtitle} ${LABEL[props.imageKey]}`} />
                <button className={`${styles.navB} ${styles.r}`} type="button" aria-label="다음 이미지" onClick={() => step(1)}>›</button>
            </div>
            <div className={styles.mfoot} role="tablist">
                {ORDER_K.map((k, i) => (
                    <button key={k} className={styles.mtab} role="tab" type="button" aria-selected={k === props.imageKey} onClick={() => props.onChange(k)}>
                        {`${i + 1} ${LABEL[k]}`}
                    </button>
                ))}
            </div>
        </div>,
        document.body
    );
};

export { DownloadIcon };
export default MealPlanPreviewModal;
